import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui-custom/Card";
import { Badge } from "../../../components/ui-custom/Badge";
import { AlertTriangle, TrendingDown, TrendingUp, IndianRupee, BarChart3, RefreshCw } from "lucide-react";
import { formatCurrency, getSectionTotals, getAllTotals } from "./boqData";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const BoQAnalytics = ({ sections, refreshing }) => {
  const totals = getAllTotals(sections);
  const overallVariance = totals.totalBudget - totals.totalActual;
  const budgetConsumed = totals.totalBudget > 0 ? Math.round((totals.totalActual / totals.totalBudget) * 100) : 0;

  const sectionData = sections.map((section) => {
    const totalsBySection = getSectionTotals(section);
    const variance = totalsBySection.totalBudget - totalsBySection.totalActual;
    const variancePercent = totalsBySection.totalBudget > 0 ? Math.round((variance / totalsBySection.totalBudget) * 100) : 0;
    const profit = totalsBySection.totalBudget > 0 ? ((totalsBySection.totalBudget - totalsBySection.totalActual) / totalsBySection.totalBudget) * 100 : 0;

    let statusLabel = "Under Budget";
    let statusColor = "text-success";
    if (variancePercent < -10) {
      statusLabel = "Over Budget";
      statusColor = "text-destructive";
    } else if (variancePercent < 0) {
      statusLabel = "At Risk";
      statusColor = "text-warning";
    }

    return {
      ...section,
      ...totalsBySection,
      variance,
      variancePercent,
      profit,
      statusLabel,
      statusColor,
    };
  });

  const alerts = [];
  sectionData.forEach((section) => {
    if (section.variancePercent < -10) {
      alerts.push({ message: `${section.name}: Over budget by ${Math.abs(section.variancePercent)}%`, type: "error" });
    } else if (section.variancePercent < 0) {
      alerts.push({ message: `${section.name}: Budget risk - ${Math.abs(section.variancePercent)}% over`, type: "warning" });
    }
  });

  const chartData = sectionData.map((section) => ({
    name: section.code,
    budget: section.totalBudget / 1000,
    actual: section.totalActual / 1000,
  }));

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-end gap-2 text-xs text-muted-foreground">
        {refreshing && <RefreshCw className="h-3.5 w-3.5 animate-spin" />}
        <span>Analytics are based on live BoQ estimates and actuals</span>
      </div>

      <div className="grid grid-cols-4 gap-4">
        {[
          {
            label: "TOTAL BUDGET",
            value: formatCurrency(totals.totalBudget),
            indicator: "Allocated",
            indicatorColor: "text-sm-primary",
            icon: IndianRupee,
          },
          {
            label: "ACTUAL SPENT",
            value: formatCurrency(totals.totalActual),
            indicator: `${budgetConsumed}% used`,
            indicatorColor: "text-warning",
            icon: IndianRupee,
          },
          {
            label: "VARIANCE",
            value: formatCurrency(Math.abs(overallVariance)),
            indicator: overallVariance >= 0 ? "Under Budget" : "Over Budget",
            indicatorColor: overallVariance >= 0 ? "text-success" : "text-destructive",
            icon: overallVariance >= 0 ? TrendingUp : TrendingDown,
          },
          {
            label: "BUDGET CONSUMED",
            value: `${budgetConsumed}%`,
            indicator: budgetConsumed > 80 ? "High" : "Normal",
            indicatorColor: budgetConsumed > 80 ? "text-destructive" : "text-success",
            icon: BarChart3,
          },
        ].map((card) => (
          <Card key={card.label} className="border-border/60 shadow-none">
            <CardContent className="p-4">
              <p className="text-[11px] font-medium tracking-wider text-muted-foreground uppercase mb-2">{card.label}</p>
              <p className="text-xl font-bold text-foreground">{card.value}</p>
              <div className={`flex items-center gap-1 mt-1.5 ${card.indicatorColor}`}>
                <card.icon className="h-3 w-3" />
                <span className="text-[11px] font-medium">{card.indicator}</span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {alerts.length > 0 && (
        <div className="space-y-2">
          {alerts.map((alert, index) => (
            <div
              key={`${alert.message}-${index}`}
              className={`flex items-center gap-3 rounded-lg border p-3 ${
                alert.type === "error" ? "border-destructive/30 bg-destructive/5" : "border-warning/30 bg-warning/5"
              }`}
            >
              <AlertTriangle className={`h-4 w-4 shrink-0 ${alert.type === "error" ? "text-destructive" : "text-warning"}`} />
              <p className="text-sm text-foreground">{alert.message}</p>
            </div>
          ))}
        </div>
      )}

      <Card className="border-border/60 shadow-none">
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-sm-secondary" /> Budget vs Actual (Rs. in K)
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          {chartData.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">No data available yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData} barGap={4}>
                <XAxis dataKey="name" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ fontSize: 11, borderRadius: 8, padding: "8px 12px" }}
                  formatter={(value) => [`₹${Number(value).toFixed(1)}K`]}
                />
                <Bar dataKey="budget" name="Budget" fill="#8067E0" radius={[4, 4, 0, 0]} />
                <Bar dataKey="actual" name="Actual" fill="#FF7C01" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <Card className="border-border/60 shadow-none">
        <CardHeader className="pb-2 pt-4 px-4">
          <CardTitle className="text-sm font-medium">Section-wise Breakdown</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="grid grid-cols-[1fr_100px_100px_100px_80px_90px] gap-2 px-4 py-2.5 bg-muted/30 text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
            <span>Section</span>
            <span className="text-right">Budget</span>
            <span className="text-right">Actual</span>
            <span className="text-right">Variance</span>
            <span className="text-right">Profit</span>
            <span className="text-center">Risk</span>
          </div>
          {sectionData.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">No sections found</div>
          ) : (
            sectionData.map((section) => (
              <div
                key={section.id}
                className="grid grid-cols-[1fr_100px_100px_100px_80px_90px] gap-2 px-4 py-3 border-t border-border/20 items-center text-sm"
              >
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono text-sm-primary bg-sm-primary/10 px-1.5 py-0.5 rounded font-medium">
                    {section.code}
                  </span>
                  <span className="text-sm font-medium truncate uppercase">{section.name}</span>
                </div>
                <span className="text-right">{formatCurrency(section.totalBudget)}</span>
                <span className="text-right">{formatCurrency(section.totalActual)}</span>
                <span className={`text-right font-medium ${section.statusColor}`}>{formatCurrency(Math.abs(section.variance))}</span>
                <span className="text-right">{section.profit.toFixed(1)}%</span>
                <div className="text-center">
                  <Badge
                    className={`text-[10px] px-1.5 py-0 ${
                      section.statusLabel === "Over Budget"
                        ? "bg-destructive/10 text-destructive"
                        : section.statusLabel === "At Risk"
                          ? "bg-warning/10 text-warning"
                          : "bg-success/10 text-success"
                    }`}
                  >
                    {section.statusLabel}
                  </Badge>
                </div>
              </div>
            ))
          )}
          <div className="grid grid-cols-[1fr_100px_100px_100px_80px_90px] gap-2 px-4 py-3 border-t-2 border-border/40 bg-muted/20 items-center text-sm font-semibold">
            <span>TOTAL</span>
            <span className="text-right">{formatCurrency(totals.totalBudget)}</span>
            <span className="text-right">{formatCurrency(totals.totalActual)}</span>
            <span className={`text-right ${overallVariance >= 0 ? "text-success" : "text-destructive"}`}>
              {formatCurrency(Math.abs(overallVariance))}
            </span>
            <span className="text-right">{totals.avgProfit.toFixed(1)}%</span>
            <span />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BoQAnalytics;
